import React from 'react';
import Stack from '@mui/material/Stack';
import { PostCardInfo } from 'types/post';
import PostGrid from '../PostGrid/PostGrid';
import { QueryResult, SectionHeader } from '@elevator/ui';

/**
 * PopularPostList Component
 *
 * Displays a list of trending/popular posts using the PostGrid component.
 * Acts as a wrapper around PostGrid with specific configuration for the
 * "Trending" section. Supports both small (single column) and main (grid) variants.
 *
 * Features:
 * - Displays trending posts in configurable layout
 * - Uses PostGrid component for rendering
 * - Shows loading and error states
 * - Limits to 6 posts
 * - Supports small (single column) and main (grid) variants
 *
 * Used in:
 * - Home page sidebar (small variant)
 * - Post detail pages below content (main variant)
 * - Sidebar component (small variant)
 *
 * @param props - Component props extending QueryResult
 * @param props.posts - Array of post card information
 * @param props.error - Error object if posts failed to load
 * @param props.loading - Boolean indicating if posts are loading
 * @param props.variant - Layout variant: 'small' (single column) or 'main' (grid). Defaults to 'small'.
 */
const PopularPostList = ({
  posts,
  error,
  loading,
  variant = 'small',
}: PopularPostListPropTypes): React.JSX.Element => {
  return (
    <section data-component="PopularPostList">
      <Stack spacing={1} width="100%">
        <SectionHeader title="Trending" />
        <PostGrid
          posts={posts}
          loadLimit={6}
          isLoading={loading}
          error={error}
          loadingMore={false}
          onLoadMore={null}
          variant={variant}
        />
      </Stack>
    </section>
  );
};

export interface PopularPostListPropTypes
  extends QueryResult<'posts', Array<PostCardInfo>> {
  /**
   * Layout variant for the post grid.
   * - 'small': Single column layout (for sidebars)
   * - 'main': Responsive grid layout (for full-width content areas)
   * @default 'small'
   */
  variant?: 'main' | 'small';
}

export default PopularPostList;
